import React from 'react';
import { styled } from '@linaria/react';

import { range, normalize, clamp } from '@/utils';
import useMousePositionWithinElement from '@/hooks/use-mouse-position-within-element';

const NUM_OF_STOPS = 10;

function ChannelSlider({
  label,
  color,
  channel,
  value,
  dynamicGradients,
  onChange,
}) {
  const wrapperRef = React.useRef();

  const id = React.useId();

  const [mousePosition, boundingBox] = useMousePositionWithinElement(
    wrapperRef,
    { buffer: 128 }
  );
  const [isMouseDown, setIsMouseDown] = React.useState(false);

  React.useEffect(() => {
    if (
      !isMouseDown ||
      typeof mousePosition.x !== 'number' ||
      typeof mousePosition.y !== 'number' ||
      !boundingBox
    ) {
      return;
    }

    let newValue = (mousePosition.x / boundingBox.width) * 255;
    newValue = Math.round(clamp(newValue, 0, 255));

    onChange(channel, newValue);
  }, [isMouseDown, mousePosition, boundingBox, channel, onChange]);

  React.useEffect(() => {
    function handleMouseUp() {
      setIsMouseDown(false);
    }

    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('touchend', handleMouseUp);
    document.addEventListener('mouseleave', handleMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchend', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isMouseDown]);

  function handleKeyDown(ev) {
    switch (ev.key) {
      case 'ArrowLeft': {
        ev.preventDefault();
        onChange(channel, clamp(value - 10, 0, 255));
        break;
      }
      case 'ArrowRight': {
        ev.preventDefault();
        onChange(channel, clamp(value + 10, 0, 255));
        break;
      }
    }
  }

  const stops = range(NUM_OF_STOPS).map((index) => {
    // If `dynamicGradients` is true, we want the slider to show
    // the range of possible colors for this slider. Otherwise, it
    // should always be a slider from black to this color.
    const colorObj = dynamicGradients
      ? { ...color }
      : { r: 0, g: 0, b: 0 };

    colorObj[channel] = normalize(index, 0, NUM_OF_STOPS - 1, 0, 255);

    return formatColor(colorObj);
  });

  const handlePercentage = normalize(value, 0, 255, 0, 100);

  return (
    <Wrapper>
      <TopRow>
        <Label htmlFor={`slider-${id}`}>{label}</Label>
        <Value>{value}</Value>
      </TopRow>
      <SliderButton
        ref={wrapperRef}
        id={`slider-${id}`}
        style={{ background: formatGradient(stops) }}
        onMouseDown={() => setIsMouseDown(true)}
        onTouchStart={() => setIsMouseDown(true)}
        onKeyDown={handleKeyDown}
      >
        <HandleWrapper>
          <Handle
            style={{
              left: `${handlePercentage}%`,
            }}
          />
        </HandleWrapper>
      </SliderButton>
    </Wrapper>
  );
}

const formatColor = ({ r, g, b }) => `rgb(${r} ${g} ${b})`;

const formatGradient = (stops) => {
  return `linear-gradient(90deg, ${stops.join(', ')})`;
};

const Wrapper = styled.div``;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 4px;
  height: 2rem;
`;

const Label = styled.label`
  font-size: 1.125rem;
`;
const Value = styled.div`
  font-size: 1.25rem;
  font-weight: var(--font-weight-bold);
`;

const SliderButton = styled.button`
  position: relative;
  display: block;
  width: 100%;
  height: 36px;
  border-radius: 4px;
  border: 2px solid white;
  box-shadow:
    0.6px 1.3px 2.2px rgba(0, 0, 0, 0.02),
    1.5px 3.1px 5.3px rgba(0, 0, 0, 0.028),
    2.9px 5.8px 10px rgba(0, 0, 0, 0.035),
    5.1px 10.3px 17.9px rgba(0, 0, 0, 0.042),
    9.6px 19.2px 33.4px rgba(0, 0, 0, 0.05),
    23px 46px 80px rgba(0, 0, 0, 0.07);
  user-select: none;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;

  &:focus {
    outline: none;
  }
`;

const HandleWrapper = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: calc(100% - 16px);
  height: 100%;
`;

const Handle = styled.span`
  position: absolute;
  top: -4px;
  display: block;
  width: 16px;
  height: calc(100% + 8px);
  border: 2px solid black;
  box-shadow: inset 0px 0px 0px 2px white;
  border-radius: 1px;

  ${SliderButton}:focus-visible & {
    outline-offset: 8px;
    outline: 2px auto;
    outline-color: Highlight;
    outline-color: -webkit-focus-ring-color;
  }
`;

export default ChannelSlider;
